import {
  MOCK_DURATION_SECONDS,
  MOCK_QUESTIONS_PER_MODULE,
  MOCK_MINUTES_PER_QUESTION,
} from "./constants";

/**
 * Build module info maps from course levels.
 * Returns { moduleMap, parentDetails }.
 */
export function buildModuleInfo(levels, courseId) {
  const courseLevels = Object.values(levels).filter(
    (l) => l.courseId === courseId
  );
  const levelsById = {};
  courseLevels.forEach((l) => {
    levelsById[l.id] = l;
  });

  // For every level, find its top-level parent
  const moduleMap = {};
  courseLevels.forEach((level) => {
    let current = level;
    while (current.sublevelId && levelsById[current.sublevelId]) {
      current = levelsById[current.sublevelId];
    }
    moduleMap[level.id] = { name: current.name, position: current.position };
  });

  // Aggregate questionsCount per parent module
  const parents = courseLevels.filter((l) => !l.sublevelId);
  const parentDetails = {};
  parents.forEach((parent) => {
    parentDetails[parent.name] = {
      name: parent.name,
      position: parent.position,
      questionsCount: parent.questionsCount || 0,
      levelId: parent.id,
    };
  });

  return { moduleMap, parentDetails };
}

/**
 * Build a mock exam queue item for a given module.
 */
export function buildMockItem(moduleName, parentDetails, courseId) {
  const details = parentDetails[moduleName];
  if (!details || details.questionsCount <= 0) return null;

  const numQuestions = Math.min(
    details.questionsCount,
    MOCK_QUESTIONS_PER_MODULE
  );

  return {
    type: "mock",
    moduleName,
    modulePosition: details.position,
    mockData: {
      type: "mock",
      moduleName,
      moduleNumber:
        parseInt(moduleName.match(/^(\d+)/)?.[1], 10) || details.position,
      numberOfQuestions: numQuestions,
      timeBudgetMinutes: numQuestions * MOCK_MINUTES_PER_QUESTION,
      durationSeconds: MOCK_DURATION_SECONDS,
      levelIds: [details.levelId],
      courseId,
      maxQuestions: details.questionsCount,
    },
  };
}

/**
 * Build the lesson + mock queue from remaining (incomplete) lessons.
 * Inserts a mock item at each module boundary.
 */
export function buildQueue(remainingLessons, moduleMap, parentDetails, courseId) {
  const queue = [];
  let currentModuleName = null;

  remainingLessons.forEach((lesson) => {
    const modInfo = moduleMap[lesson.levelId];
    const modName = modInfo?.name;

    if (modName && modName !== currentModuleName) {
      if (currentModuleName) {
        const mock = buildMockItem(currentModuleName, parentDetails, courseId);
        if (mock) queue.push(mock);
      }
      currentModuleName = modName;
    }

    queue.push({
      type: "lesson",
      lesson,
      moduleName: modName,
      modulePosition: modInfo?.position,
    });
  });

  // Final mock for the last module
  if (currentModuleName) {
    const mock = buildMockItem(currentModuleName, parentDetails, courseId);
    if (mock) queue.push(mock);
  }

  return queue;
}
